export enum ImageFormat {
    PNG = 'image/png',
    JPEG = 'image/jpeg',
}

interface IConverterOptions {
    isLimitSize: boolean,
    quality?: number,
}

const CONVERTER_MAX_WIDTH = 1600;
const CONVERTER_MAX_HEIGHT = 900;

class ImageConverter {
    public static convertTo(
        url: string,
        format: ImageFormat,
        { isLimitSize, quality }: IConverterOptions = { isLimitSize: false },
    ): Promise<Blob> {
        const image = new Image();
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        return new Promise((resolve, reject) => {
            if (!ctx) {
                reject(new Error('failed to get canvas2d context'));
                return;
            }

            image.addEventListener('load', () => {
                const { width, height } = image;
                if (isLimitSize) {
                    const { width: newWidth, height: newHeight } = this.getNewSizeByLimit(
                        width,
                        height,
                        CONVERTER_MAX_WIDTH,
                        CONVERTER_MAX_HEIGHT,
                    );
                    canvas.width = newWidth;
                    canvas.height = newHeight;
                    ctx.drawImage(image, 0, 0, newWidth, newHeight);
                } else {
                    canvas.width = width;
                    canvas.height = height;
                    ctx.drawImage(image, 0, 0, width, height);
                }
                canvas.toBlob((blob) => {
                    if (!blob) {
                        reject(new Error('failed to transform canvas to blob'));
                        return;
                    }
                    resolve(blob);
                }, format, quality);
            });

            image.crossOrigin = 'anonymous';
            image.src = url;
        });
    }

    /**
     * 转换 DataUrl 为 Blob 对象，
     * 参见：https://developer.mozilla.org/en-US/docs/Web/HTTP/Basics_of_HTTP/Data_URLs
     */
    public static convertDataUriToBlob(dataURI: string): Blob | null {
        const match = /data:([\w/+]+)(;base64)?,(.*)/.exec(dataURI);
        if (!match) return null;

        const [, mime, isBase64, data] = match;

        const byteString = isBase64 ? atob(data) : decodeURIComponent(data);

        const arrayBuffer = new ArrayBuffer(byteString.length);
        const unit8Array = new Uint8Array(arrayBuffer);
        for (let i = 0; i < byteString.length; i++) {
            unit8Array[i] = byteString.charCodeAt(i);
        }

        return new Blob([arrayBuffer], { type: mime });
    }

    /**
     * 按最大宽高缩放尺寸
     * @param width 原始宽度
     * @param height 原始高度
     * @param maxWidth 最大宽度
     * @param maxHeight 最大高度
     * @returns 缩放后的宽高
     */
    private static getNewSizeByLimit(
        width: number,
        height: number,
        maxWidth: number,
        maxHeight: number,
    ): { width: number; height: number } {
        // 宽高没有超过限制，直接返回
        if (width <= maxWidth && height <= maxHeight) {
            return { width, height };
        }
        // 按最大宽度等比例缩放
        if (width / height > maxWidth / maxHeight) {
            return { width: maxWidth, height: Math.ceil((maxWidth / width) * height) };
        }
        // 按最大高度等比例缩放
        return { width: Math.ceil((maxHeight / height) * width), height: maxHeight };
    }
}

export default ImageConverter;
